<template>
  <div class="monitor container is-fluid">
    <div class="columns mt-0">
      <div class="column is-6">
        <h4 class="is-size-4-desktop">Monitor de pedidos ({{ modo }})</h4>
        <h4 v-if="bodega" class="is-size-6-desktop">
          Sucursal: {{ bodega.sucursal.nombre }} - {{ bodega.nombre }}
        </h4>
        <div v-else class="notification is-info">
          El usuario identificado no se encuentra asignado a una sucursal y/o
          bodega. Favor contactar al administrador.
        </div>
      </div>
      <div class="column">
        <b-button
          class="is-pulled-right"
          icon-left="filter"
          @click="openFilterModalHandle"
        >
          Filtros
        </b-button>
      </div>
    </div>
    <div v-if="bodega" class="columns">
      <div class="column is-12">
        <pedidos :items="pedidos" :modo="modo" />
      </div>
    </div>
    <b-modal
      :active.sync="isModalOpened"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
      @close="closeFilterModalHandle"
    >
      <filtros :modo-monitor="modo" @close="closeFilterModalHandle" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as signalR from '@microsoft/signalr'

import Filtros from '@/components/monitorUsuario/Filtros.vue'
import Pedidos from '@/components/monitorUsuario/Pedidos.vue'

export default {
  name: 'Monitor',
  components: {
    Filtros,
    Pedidos
  },
  props: {
    modo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      connection: null,
      reconectando: false,
      isModalOpened: false
    }
  },
  computed: {
    ...mapGetters('sesion', ['bodega']),
    pedidos() {
      switch (this.modo) {
        case 'despacho':
          return this.$store.state.monitorDespacho.pedidos
        case 'posterior':
          return this.$store.state.monitorPosteriores.pedidos
        case 'picking':
        default:
          return this.$store.state.monitor.pedidos
      }
    },
    hubWebsocket() {
      switch (this.modo) {
        case 'despacho':
          return 'despachos'
        case 'posterior':
          return 'posteriores'
        case 'picking':
        default:
          return 'pedidos'
      }
    },
    eventoActualizacion() {
      switch (this.modo) {
        case 'despacho':
          return 'UpdateCompletados'
        case 'posterior':
          return 'UpdatePosteriores'
        case 'picking':
        default:
          return 'UpdatePedidos'
      }
    }
  },
  watch: {
    bodega() {
      if (this.bodega) {
        this.getPedidos()
        this.enableHub()
      } else {
        this.stopHub()
      }
    },
    modo() {
      this.createHub()
    }
  },
  created() {
    this.createHub()
  },
  mounted() {
    if (this.bodega) {
      switch (this.modo) {
        case 'despacho':
          this.getDespachos()
          break
        case 'posterior':
          this.getPosteriores()
          break
        case 'picking':
        default:
          this.getPedidos()
      }
    }
  },
  beforeDestroy() {
    this.stopHub()
  },
  methods: {
    async createHub() {
      const self = this
      const url = `${process.env.VUE_APP_BASE_URL}hubs/${self.hubWebsocket}`
      if (this.connection) {
        await this.disableHub()
        await this.stopHub()
      }
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => localStorage.getItem('token') || ''
        })
        .withAutomaticReconnect([5000, 5000, 10000, 10000, 10000, 10000, 10000])
        .configureLogging(
          process.env.NODE_ENV === 'development'
            ? signalR.LogLevel.Information
            : signalR.LogLevel.Error
        )
        .build()
      this.connection.onreconnecting(() => (self.reconectando = true))
      this.connection.onreconnected(() => (self.reconectando = false))
      this.connection.onclose(() => {
        if (
          self.connection &&
          self.connection.state === 'Disconnected' &&
          self.reconectando
        ) {
          self.notificarError(
            'Se perdió la conexión con el servidor. Se volverá a intentar luego.'
          )
          setTimeout(() => {
            self.startHub()
          }, 30000)
        }
      })
      this.startHub().then((_) => this.enableHub())
    },
    startHub() {
      const self = this
      return this.connection
        .start()
        .then((_) => {
          self.connection
            .invoke('conectar-bodega', `${self.bodega.id}`)
            .catch((err) => {
              self.notificarError(err.toString())
            })
          self.$nextTick(() => self.getPedidos())
        })
        .catch((err) => {
          if (err.statusCode === 401) {
            if (!self.$store.getters['sesion/esValida']) {
              this.$router.push('/login').catch(() => {})
            } else {
              return self.$store.dispatch('sesion/refreshToken')
            }
          } else {
            self.procesarError(err)
          }
        })
    },
    async stopHub() {
      const self = this
      if (this.connection.state !== 'Disconnected')
        await this.connection
          .invoke('desconectar-bodega', `${self.bodega.id}`)
          .catch((err) => {
            self.notificarError(err.toString())
          })
          .then(async (_) => self.connection.stop())
    },
    async disableHub() {
      const self = this
      await self.connection.off(self.eventoActualizacion)
    },
    enableHub() {
      const self = this
      switch (this.modo) {
        case 'despacho':
          self.connection.on('UpdateCompletados', () =>
            self.$nextTick(() => self.getDespachos())
          )
          break
        case 'posterior':
          self.connection.on('UpdatePosteriores', () =>
            self.$nextTick(() => self.getPosteriores())
          )
          break
        case 'picking':
        default:
          self.connection.on('UpdatePedidos', () =>
            self.$nextTick(() => self.getPedidos())
          )
      }
    },
    getPedidos() {
      return this.$store
        .dispatch('monitor/updateTodos')
        .catch((error) => this.procesarError(error))
    },
    getDespachos() {
      return this.$store
        .dispatch('monitorDespacho/updateCompletados')
        .catch((error) => this.procesarError(error))
    },
    getPosteriores() {
      return this.$store
        .dispatch('monitorPosteriores/updatePosteriores')
        .catch((error) => this.procesarError(error))
    },
    openFilterModalHandle() {
      this.isModalOpened = true
    },
    closeFilterModalHandle() {
      this.isModalOpened = false
    }
  }
}
</script>
